import { Injectable, inject } from "@angular/core";
import {
  doc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit as fsLimit,
  Timestamp,
  Firestore,
  QueryConstraint,
} from "@angular/fire/firestore";
import { FirestoreService } from "./firestore.service";
import { BillAuto, ClientSnapshot, COL } from "./types";
import { ClientsService } from "./clients.service";
import { buildClientSnapshot } from "./legacy-mappers";

/**
 * Sane cap for the per-client auto-bill list. In practice clients have 1–3
 * recurring templates; the cap keeps us safe if data ever drifts.
 */
const BILLS_AUTO_PER_CLIENT_CAP = 100;

@Injectable({ providedIn: "root" })
export class BillsAutoService {
  private readonly fs = inject(FirestoreService);
  private readonly db = inject(Firestore);
  private readonly clients = inject(ClientsService);

  /** Recurring bill templates for one client (newest first). */
  async findByClient(
    clientId: string,
    opts?: { limit?: number }
  ): Promise<BillAuto[]> {
    const ref = this.fs.col<BillAuto>(COL.billsAuto);
    const constraints: QueryConstraint[] = [];
    constraints.push(where("clientId", "==", clientId));
    constraints.push(orderBy("createdAt", "desc"));
    constraints.push(fsLimit(opts?.limit ?? BILLS_AUTO_PER_CLIENT_CAP));
    const snap = await getDocs(query(ref, ...constraints));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as BillAuto));
  }

  async create(
    input: {
      clientId: string;
      mount: number;
      notes?: string;
      isActive?: boolean;
      generationDay?: number;
      clientSnapshot?: ClientSnapshot;
    },
    creator: { uid: string; name: string }
  ): Promise<BillAuto> {
    let snapshot = input.clientSnapshot;
    if (!snapshot) {
      const client = await this.clients.getById(input.clientId);
      if (!client) throw new Error("Cliente no encontrado");
      snapshot = buildClientSnapshot(client);
    }

    const id = doc(this.fs.col(COL.billsAuto)).id;
    const ref = this.fs.docRef<BillAuto>(COL.billsAuto, id);
    const generationDay = this.normalizeGenerationDay(input.generationDay);
    const isActive = input.isActive ?? true;
    const mount = Number(input.mount);
    const notes = input.notes ?? null;

    const body: Omit<BillAuto, "id"> = {
      clientId: input.clientId,
      creatorUid: creator.uid,
      creatorName: creator.name,
      client: snapshot!,
      mount,
      notes,
      isActive,
      generationDay,
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    };

    await setDoc(ref as any, body as any);

    const localNow = Timestamp.now();
    return { id, ...body, createdAt: localNow, updatedAt: localNow };
  }

  async update(
    id: string,
    patch: Partial<Pick<BillAuto, "mount" | "notes" | "isActive" | "generationDay">>
  ): Promise<{ id: string } & Partial<BillAuto>> {
    const ref = this.fs.docRef<BillAuto>(COL.billsAuto, id);
    const normalized: Record<string, any> = { updatedAt: serverTimestamp() };
    if (patch.mount !== undefined) normalized.mount = Number(patch.mount);
    if (patch.notes !== undefined) normalized.notes = patch.notes;
    if (patch.isActive !== undefined) normalized.isActive = patch.isActive;
    if (patch.generationDay !== undefined) {
      normalized.generationDay = this.normalizeGenerationDay(patch.generationDay);
    }
    await updateDoc(ref as any, normalized);
    // Skip the post-write read; callers patch the row in NgxS state with the
    // values they just sent.
    return { id, ...patch };
  }

  private normalizeGenerationDay(day?: number): number {
    const n = Number(day ?? 1);
    if (!Number.isFinite(n)) return 1;
    return Math.max(1, Math.min(28, Math.trunc(n)));
  }
}
